'use client'

import { SplitsAvatar } from '~/components/SplitsAvatar'
import { Copyable, List, ListItemProps } from '~/components/component-library'
import { Timestamp } from '~/components/component-library/Timestamp'
import { useAuthUser } from '~/hooks/useAuthUser'
import { BackendUserEoaSigner } from '~/types'
import { EvmAddress } from '~/utils/address'
import { shortenAddress } from '~/utils/addresses'

export const EoaSigningKeysList = () => {
  const { user } = useAuthUser()
  const eoaSigners = user?.eoaSigners ?? []

  if (eoaSigners.length === 0) return null

  return (
    <List
      label="EOA signers"
      description="Wallets registered to you that can sign transactions via the API or CLI"
      items={[...eoaSigners]
        .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
        .map(toListItem)}
    />
  )
}

const toListItem = (signer: BackendUserEoaSigner): ListItemProps => {
  const base = {
    key: signer.id,
    image: (
      <SplitsAvatar address={EvmAddress.format(signer.address)} size={20} />
    ),
    content: signer.name ?? shortenAddress(signer.address),
    description: signer.name ? (
      <Copyable
        layout="standard"
        value={signer.address}
        truncateMiddle={4}
        size="small"
      />
    ) : undefined,
    meta: (
      <span>
        {signer.lastVerifiedAt ? 'Last verified' : 'Added'}{' '}
        <Timestamp
          timestamp={signer.lastVerifiedAt ?? signer.createdAt}
          type="relative"
          abbreviate
          showTooltip={false}
        />
      </span>
    ),
  }

  if (signer.accounts.length === 0) return base

  return {
    ...base,
    expandableContent: (
      <List
        items={signer.accounts.map((account) => ({
          key: account.id,
          content: account.name,
          description: shortenAddress(account.address),
          meta: account.orgName,
        }))}
      />
    ),
  }
}
